import { styled } from '@mui/material/styles';
import React from 'react';
import { GivelifyButton } from '../button';
import { GivelifyLabel } from '../label';
import {
    GivelifyColor,
    GivelifyColorPalette,
} from '../style/givelifyDefaultTheme';

type Colors = {
    background: GivelifyColor;
    border: GivelifyColor;
};
export type StateType = 'info' | 'success' | 'warning' | 'error';

const stateColors: Record<StateType, Colors> = {
    info: { background: 'info100', border: 'info200' },
    success: { background: 'success100', border: 'success200' },
    warning: { background: 'warning100', border: 'warning200' },
    error: { background: 'error100', border: 'error200' },
};

const NotificationRoot = styled('div', {
    shouldForwardProp: (prop) => prop !== 'colors' && prop !== 'hasCloseButton',
})<{ colors: Colors; hasCloseButton: boolean }>(
    ({ colors, hasCloseButton }) => ({
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: GivelifyColorPalette[colors.background],
        borderColor: GivelifyColorPalette[colors.border],
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 6,
        paddingLeft: 16,
        paddingTop: 16,
        paddingBottom: 18,
        paddingRight: hasCloseButton ? 2 : 16,
    }),
);

export interface GivelifyNotificationProps {
    name?: string;
    show: boolean;
    variant: StateType;
    text?: string;
    /** Close icon on the right will not appear if this is undefined. */
    onClose?: () => unknown;
    children?: React.ReactNode;
    className?: string;
}

export const GivelifyNotification: React.FCC<GivelifyNotificationProps> =
    React.memo(
        ({ name, text, variant, show, onClose, className, children }) => {
            if (!show) return null;
            return (
                <NotificationRoot
                    className={className}
                    colors={stateColors[variant]}
                    hasCloseButton={onClose !== undefined}
                >
                    {text ? <GivelifyLabel text={text} /> : children}
                    {onClose && (
                        <GivelifyButton
                            name={`${name}_close`}
                            iconVariant="close"
                            variant="ghost"
                            fontSize={14}
                            color="grey600"
                            padding={0}
                            minWidth="initial"
                            minHeight="initial"
                            onClick={onClose}
                        />
                    )}
                </NotificationRoot>
            );
        },
    );
